import { Image } from 'expo-image';
import * as React from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import Gallery from '@/components/gallery';
import PdfViewer from '@/components/pdf-viewer';
import { Text } from '@/ui';
import { Docs } from '@/ui/icons';
import { ChevronRight } from '@/ui/icons/chevron-right';
import { MenuItem } from '@/ui/menu-item';

export interface MediaPreviewerProps {
  title: string;
  pictures: string[];
  pdfs: any;
}

export default function MediaPreviewer({
  title,
  pictures,
  pdfs,
}: MediaPreviewerProps) {
  const { styles, theme } = useStyles(stylesheet);
  const [showGallery, setShowGallery] = React.useState(false);
  const [activePdfUri, setActivePdfUri] = React.useState('');
  const [activeImgIdx, setActiveImgIdx] = React.useState(0);

  return (
    <View style={{ flexGrow: 1, gap: 40 }}>
      {pictures?.length > 0 ? (
        <View style={{ gap: 8 }}>
          <Text tx="mediaViewer.pictures" type="title2" />

          <ScrollView
            horizontal
            contentContainerStyle={styles.picturesContainer}
            showsHorizontalScrollIndicator={false}
          >
            {pictures?.map((picture: string, idx: number) => (
              <View key={`${picture}-${idx}`} style={{ width: 130 }}>
                <Pressable
                  style={{
                    width: 130,
                    height: 140,
                    borderRadius: 8,
                    overflow: 'hidden',
                  }}
                  onPress={() => {
                    setShowGallery(true);
                    setActiveImgIdx(idx);
                  }}
                >
                  <Image
                    source={picture}
                    contentFit="contain"
                    style={{
                      width: '100%',
                      aspectRatio: 1,
                      borderRadius: 8,
                    }}
                  />
                </Pressable>

                <Text
                  numberOfLines={1}
                  style={{ color: theme.colors.text.secondary }}
                  type="caption1"
                >
                  {picture.split('/').pop()}
                </Text>
              </View>
            ))}
          </ScrollView>
        </View>
      ) : null}

      {pdfs.length > 0 ? (
        <View style={{ gap: 8 }}>
          <Text tx="mediaViewer.pdf" type="title2" />

          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{ gap: 8 }}
          >
            {pdfs.map((pdf: string, idx: number) => (
              <MenuItem
                key={`${pdf}-${idx}`}
                leftIcon={<Docs />}
                leftIconFillColor={theme.colors.icon.color}
                rightIcon={<ChevronRight />}
                tx="mediaViewer.pdfName"
                values={{ pdfName: pdf.split('/').pop() }}
                onPress={() => {
                  setActivePdfUri(pdf);
                }}
              />
            ))}
          </ScrollView>
        </View>
      ) : null}

      <Gallery
        navTitle={title}
        data={pictures}
        onDismiss={() => setShowGallery(false)}
        initialIndex={activeImgIdx}
        visible={Boolean(showGallery && pictures)}
      />

      <PdfViewer
        navTitle={title}
        uri={activePdfUri}
        onDismiss={() => setActivePdfUri('')}
        visible={activePdfUri !== ''}
      />
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  picturesContainer: {
    gap: 16,
  },
}));
